import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Shield, CheckCircle, AlertCircle, Phone } from "lucide-react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

type WarrantyResult = "" | "found" | "not-found";

export default function Warranty() {
  const ref = useInView();
  const [serial, setSerial] = useState("");
  const [result, setResult] = useState<WarrantyResult>("");

  const handleCheck = (e: React.FormEvent) => {
    e.preventDefault();
    if (serial.trim()) {
      setResult("not-found");
    }
  };

  return (
    <div ref={ref}>
      <section className="pt-32 pb-20 section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Warranty Verification</p>
            <h1 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Check Your <span className="gradient-text">Warranty</span>
            </h1>
            <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed">
              Verify the warranty status of your K-Tec product by entering the serial number.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-card border border-brand-border/50 overflow-hidden">
              <div className="w-16 h-16 bg-brand-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield size={32} className="text-brand-orange" />
              </div>
              <h2 className="font-display text-2xl font-bold text-center mb-2">Enter Serial Number</h2>
              <p className="text-brand-muted text-center mb-8">
                Find the serial number on your product or installation certificate.
              </p>
              <form onSubmit={handleCheck} className="space-y-6">
                <div className="relative">
                  <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) => { setSerial(e.target.value); setResult(""); }}
                    placeholder="Enter serial number (e.g., KTEC-2025-XXXX)"
                    className="w-full pl-12 pr-4 py-4 bg-brand-gray rounded-2xl border-2 border-transparent focus:border-brand-orange focus:bg-white outline-none transition-all text-base font-medium"
                  />
                </div>
                <button type="submit" className="btn-primary w-full">
                  Check Warranty
                </button>
              </form>
              {result === "not-found" && (
                <div className="mt-6 p-4 bg-[#F1F6EE] rounded-2xl flex items-start gap-3">
                  <AlertCircle size={20} className="text-brand-orange mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Serial number not found</p>
                    <p className="text-sm text-brand-muted mt-1">
                      Please check your serial number and try again, or contact our support team.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
              <div className="animate-on-scroll stagger-1 bg-brand-gray rounded-2xl p-6">
                <h3 className="font-semibold mb-2">Product Warranty</h3>
                <p className="text-sm text-brand-muted leading-relaxed">
                  All K-Tec products come with a 2-year manufacturer warranty covering defects in materials and workmanship.
                </p>
              </div>
              <div className="animate-on-scroll stagger-2 bg-brand-gray rounded-2xl p-6">
                <h3 className="font-semibold mb-2">Service Warranty</h3>
                <p className="text-sm text-brand-muted leading-relaxed">
                  Our installation and configuration services are covered by a 3-year service warranty.
                </p>
              </div>
            </div>
            <div className="animate-on-scroll stagger-3 mt-12 text-center bg-brand-orange/5 rounded-3xl p-8">
              <p className="text-sm text-brand-muted mb-4">Need help with warranty?</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:+94762829800" className="btn-primary !py-3">
                  <Phone size={16} className="mr-2" /> Call Support
                </a>
                <a href="https://wa.me/94702161323" target="_blank" rel="noopener noreferrer" className="btn-outline !py-3">
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
